import { Report_ElementQueryOptionsInput, Report_ElementType } from "@/services/graphql/reactQuery";
import {
  PaxTableCell,
  PaxTableColumnHeader,
  PaxTableRow,
  ReportElementData,
} from "@/shared/types/element";
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  TypographyProps,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  PaxElementWrapper,
  PaxElementWrapperProps,
  PaxTable,
  PaxTableProps,
  TooltipCell,
} from "@ncino/pax-ui";
import { useNavigate, UseNavigateResult } from "@tanstack/react-router";
import {
  getDisplayForCell,
  getPrecisionRoundingFromSettings,
  PrecisionRoundingConfig,
  renderHeaderDisplayName,
  resolveElementData,
  toInternalPath,
} from "./util";

interface PaxMatrixElementBaseProps {
  elementData: ReportElementData;
  tableHeaderVariant?: "dark" | "light";
  nullDisplay?: string;
}

export type PaxMatrixElementProps = PaxMatrixElementBaseProps &
  Omit<PaxElementWrapperProps, "children"> &
  Omit<PaxTableProps, "children" | "pagination"> & { isLoading?: boolean };

export default function PaxMatrixElement({
  title = "Matrix",
  showHeader,
  paragraph,
  note,
  sx,
  isLoading,
  onMoreOptions,
  onEdit,
  elevated,
  customHeader,
  ...paxMatrixProps
}: PaxMatrixElementProps) {
  const elementWrapperProps = {
    title,
    showHeader,
    paragraph,
    note,
    onMoreOptions,
    onEdit,
    elevated,
    sx,
    customHeader,
  };
  const navigate = useNavigate();
  const theme = useTheme();
  const isEmpty = false; // todo: logic for this later
  return (
    <PaxElementWrapper {...elementWrapperProps}>
      <PaxTable
        {...paxMatrixProps}
        isEmpty={isEmpty}
        striped={paxMatrixProps.striped}
        stripeColor={paxMatrixProps.stripeColor}
        sx={{
          "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
            borderBottom: "none",
          },
        }}
      >
        {resolveElementData(
          Report_ElementType.Matrix,
          paxMatrixProps.elementData,
          paxMatrixProps,
          theme,
          undefined,
          isLoading,
          undefined,
          navigate
        )}
      </PaxTable>
    </PaxElementWrapper>
  );
}

interface MatrixCellDisplayProps {
  theme: Theme;
  navigate: UseNavigateResult<string>;
  displayValue?: string | null;
  href?: string | null;
  bold?: boolean | null;
}

function MatrixCellDisplay({ theme, displayValue, href, bold, navigate }: MatrixCellDisplayProps) {
  const hrefStyling = {
    color: theme.palette.info.main,
    "&:hover": {
      textDecoration: "underline",
      cursor: "pointer",
    },
  };

  const handleNavigate = (targetHref?: string | null) => {
    if (!targetHref) return;
    const path = toInternalPath(targetHref);
    if (!path) return; // not one of ours — drop it rather than falling back to window.location
    navigate({ to: path });
  };
  return (
    <Box
      sx={{
        fontWeight: bold ? 600 : "normal",
        ...(href ? hrefStyling : {}),
      }}
      onClick={() => handleNavigate(href)}
    >
      {displayValue}
    </Box>
  );
}

interface MatrixCellProps extends PaxTableCell {
  isTotal?: boolean;
  theme: Theme;
  align: "left" | "right";
  nullDisplay?: string;
  precisionRoundingConfig?: PrecisionRoundingConfig;
  navigate: UseNavigateResult<string>;
}
function MatrixCell(cell: MatrixCellProps) {
  const { theme, children, isTotal, bold, href, align, nullDisplay, navigate } = cell || {};

  return (
    <TableCell sx={{ fontWeight: isTotal || bold ? 600 : null }} align={align}>
      {isTotal ? (
        <MatrixCellDisplay
          {...{ theme, displayValue: getDisplayForCell(cell), href, bold, navigate }}
        />
      ) : null}
      {children && (
        <Box>
          {children.map((child, i) => (
            <MatrixCellDisplay
              key={`child-cell-${i}`}
              href={child.href}
              bold={child.bold}
              displayValue={getDisplayForCell(child) ?? nullDisplay}
              theme={theme}
              navigate={navigate}
            />
          ))}
        </Box>
      )}
    </TableCell>
  );
}

interface MatrixRowProps extends PaxTableRow {
  theme: Theme;
  nullDisplay?: string;
  precisionRoundingConfig?: PrecisionRoundingConfig;
  navigate: UseNavigateResult<string>;
}
function MatrixRow(row: MatrixRowProps) {
  const { cells, theme, nullDisplay } = row || {};
  const hasChildren = cells.some((c) => !!c.children);

  return (
    <>
      <TableRow
        sx={{
          backgroundColor: hasChildren
            ? theme.palette.background.default
            : theme.palette.background.paper,
        }}
      >
        {cells.map((c, i) => (
          <TableCell align={i === 0 ? "left" : "right"} key={`cell-dv-${i}`}>
            <MatrixCellDisplay
              key={`cell-dvc-${i}`}
              displayValue={getDisplayForCell(c, row.precisionRoundingConfig)}
              bold={c.bold}
              href={c.href}
              theme={theme}
              navigate={row.navigate}
            />
          </TableCell>
        ))}
      </TableRow>
      {hasChildren && (
        <TableRow>
          {cells.map((c, i) => (
            <MatrixCell
              {...c}
              key={`child-cell-${i}`}
              theme={theme}
              nullDisplay={nullDisplay}
              align={i === 0 ? "left" : "right"}
              precisionRoundingConfig={row.precisionRoundingConfig}
              navigate={row.navigate}
            />
          ))}
        </TableRow>
      )}
    </>
  );
}

export function resolveMatrixData(
  elementData: ReportElementData,
  props: PaxTableProps & {
    tableHeaderVariant?: "dark" | "light";
    nullDisplay?: string;
    settings?: Report_ElementQueryOptionsInput;
  },
  theme: Theme,
  headerProps: TypographyProps,
  navigate: UseNavigateResult<string>
): JSX.Element {
  const tableData = elementData?.tableData;
  const headers: PaxTableColumnHeader[] = tableData?.headers ?? [];
  const rows = tableData?.rows;
  return (
    <>
      <TableHead>
        <TableRow>
          {headers.map((h: PaxTableColumnHeader, i) => {
            return (
              <TooltipCell
                align={i === 0 ? "left" : "right"}
                key={`matrix-header-${i}`}
                title={h.displayName}
                sx={{ backgroundColor: `${theme.palette.background.paper} !important` }}
              >
                {renderHeaderDisplayName(h.displayName, headerProps)}
              </TooltipCell>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows?.map((r, i) => (
          <MatrixRow
            {...{
              ...r,
              index: i,
              theme,
              nullDisplay: props.nullDisplay,
              precisionRoundingConfig: getPrecisionRoundingFromSettings(props.settings),
              navigate,
            }}
            key={`matrix-row-${i}`}
          />
        ))}
      </TableBody>
    </>
  );
}
